import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { LockIcon } from './Icons';
import './LoginForm.css';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister }) => {
  const { webauthnLogin, login, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 입력 시 에러 초기화
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      return;
    }

    // 기본 로그인: 아이디/비밀번호
    const success = await login({
      username: formData.username.trim(),
      password: formData.password.trim()
    });

    if (success && onSuccess) {
      onSuccess();
    }
  };

  const handlePasskeyLogin = async () => {
    // WebAuthn 지원 확인
    if (!window.PublicKeyCredential) {
      alert('이 브라우저는 패스키를 지원하지 않습니다. 최신 브라우저를 사용해주세요.');
      return;
    }

    // 사용자명이 있으면 사용, 없으면 빈 문자열 전달 (백엔드에서 처리)
    const username = formData.username.trim() || '';
    const success = await webauthnLogin(username);
    if (success && onSuccess) {
      onSuccess();
    }
  };

  return (
    <div className="login-form-container">
      <div className="login-form">
        <h2>로그인</h2>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">계정 ID</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="계정 ID를 입력하세요"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">비밀번호</label>
            <div style={{ position: 'relative' }}>
              <input
                type={isPasswordVisible ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="비밀번호를 입력하세요"
                required
                disabled={isLoading}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onPointerDown={() => setIsPasswordVisible(true)}
                onPointerUp={() => setIsPasswordVisible(false)}
                onPointerLeave={() => setIsPasswordVisible(false)}
                onContextMenu={(e) => e.preventDefault()}
                className={`password-toggle-btn ${isPasswordVisible ? 'active' : ''}`}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px'
                }}
              >
                {isPasswordVisible ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" opacity="0.5"/>
                    <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading || !formData.username.trim() || !formData.password.trim()}
          >
            {isLoading ? '로그인 중...' : '로그인'}
          </button>

          <div className="passkey-login-section">
            <div className="divider">
              <span>또는</span>
            </div>
            <button 
              type="button"
              className="passkey-button"
              onClick={handlePasskeyLogin}
              disabled={isLoading}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><LockIcon size={16} color="currentColor" /></span> 패스키로 로그인
            </button>
            <p className="passkey-info">
              등록된 패스키(지문, Face ID, 보안 키)를 사용하여 로그인합니다.
            </p>
          </div>
        </form>

        {onSwitchToRegister && (
          <div className="switch-form">
            <p>계정이 없으신가요?</p>
            <button 
              type="button" 
              className="switch-button"
              onClick={onSwitchToRegister}
              disabled={isLoading}
            >
              회원가입
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
